import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight, CheckCircle, X, CurrencyDollar, Heart, ShoppingCart, Factory, Cloud, Truck, GraduationCap, Building } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import SEO from '../components/SEO';
import { SectionHero, SectionHeading, CTABanner, IndustryGrid } from '../components/SiteBlocks';
import { INDUSTRIES } from '../data/content';

const ICONS = { CurrencyDollar, Heart, ShoppingCart, Factory, Cloud, Truck, GraduationCap, Building };

export default function IndustriesPage() {
  const { industrySlug } = useParams();
  if (industrySlug) {
    const industry = INDUSTRIES.find(i => i.id === industrySlug);
    if (!industry) {
      return (
        <div className="min-h-screen" style={{ background: 'var(--background)' }}>
          <SEO path={`/industries/${industrySlug}`} title="Industry not found" />
          <Navbar />
          <div className="max-w-3xl mx-auto px-6 py-32 text-center">
            <h1 className="font-heading text-4xl font-bold mb-4">Industry not found</h1>
            <Link to="/industries"><Button style={{ background: 'var(--primary-accent)', color: '#fff' }}>All industries →</Button></Link>
          </div>
          <Footer />
        </div>
      );
    }
    return <IndustryDetailPage industry={industry} />;
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path="/industries" title="Industries — Deep Vertical Expertise" description="XceedAgents serves Fintech, Healthcare, E-commerce, Manufacturing, SaaS, Logistics, Education, and Real Estate — with AI agents tuned to each vertical's regulations and workflows." />
      <Navbar />

      <SectionHero eyebrow="Industries" title="Deep expertise in the verticals you care about." subtitle="From regulated finance to clinical healthcare to high-volume e-commerce — we've shipped intelligent agents and full products in every major industry." >
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }} data-testid="industries-cta">Request for Proposal <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6">
          <IndustryGrid items={INDUSTRIES} />
        </div>
      </section>

      <CTABanner title="Don't see your vertical?" subtitle="We've shipped in 40+ industries. Tell us what you do — we'll show you what we'd build." ctaText="Talk to Sales" ctaHref="/contact" />
      <Footer />
    </div>
  );
}

function IndustryDetailPage({ industry }) {
  const Icon = ICONS[industry.icon] || Building;
  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO path={`/industries/${industry.id}`} title={`${industry.name} — AI Solutions`} description={`${industry.headline} ${industry.summary}`} />
      <Navbar />

      <SectionHero eyebrow={industry.shortName} title={industry.headline} subtitle={industry.summary} testId={`industry-hero-${industry.id}`}>
        <Link to="/start-project"><Button className="h-12 px-7 font-semibold hover-lift gap-2" style={{ background: 'var(--primary-accent)', color: '#fff' }}>Request for Proposal <ArrowRight size={16} weight="bold" /></Button></Link>
      </SectionHero>

      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <div>
            <SectionHeading eyebrow="Challenges" title={`What teams in ${industry.shortName} face`} />
            <ul className="mt-6 space-y-3">
              {industry.challenges.map((c, i) => (
                <li key={i} className="flex items-start gap-3 text-base p-4 rounded-lg border" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
                  <X weight="bold" size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--error)' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <SectionHeading eyebrow="Solutions" title="What we build" />
            <ul className="mt-6 space-y-3">
              {industry.solutions.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-base p-4 rounded-lg border-2" style={{ borderColor: 'var(--primary-accent)', background: 'rgba(234,88,12,0.04)' }}>
                  <CheckCircle weight="fill" size={18} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--primary-accent)' }} />
                  <span style={{ color: 'var(--text-primary)' }}>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="py-16" style={{ background: 'var(--surface)' }}>
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Case Studies" title={`Recent wins in ${industry.shortName}`} />
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {industry.cases.map((c, i) => (
              <div key={i} className="p-7 rounded-2xl border bg-white" style={{ borderColor: 'var(--border)' }}>
                <Icon size={28} weight="duotone" style={{ color: 'var(--primary-accent)' }} className="mb-4" />
                <p className="font-heading text-lg font-bold leading-snug" style={{ color: 'var(--text-primary)' }}>{c}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeading eyebrow="Other industries" title="Where else we ship" />
          <div className="mt-10">
            <IndustryGrid items={INDUSTRIES.filter(i => i.id !== industry.id)} />
          </div>
        </div>
      </section>

      <CTABanner title={`Ready to build for ${industry.shortName}?`} ctaText="Request for Proposal" ctaHref="/start-project" />
      <Footer />
    </div>
  );
}
